import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Box,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from "@material-ui/core";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Email as EmailIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  configCard: {
    marginBottom: theme.spacing(3),
  },
  statusBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  statusSuccess: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  },
  statusError: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  },
}));

const EmailConfig = () => {
  const classes = useStyles();

  const [host, setHost] = useState("");
  const [port, setPort] = useState("587");
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [secure, setSecure] = useState(false);
  const [fromEmail, setFromEmail] = useState("");
  const [fromName, setFromName] = useState("");
  const [rateLimit, setRateLimit] = useState("60");
  
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [hasConfig, setHasConfig] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const { data } = await api.get("/api/email-config");
      const configExists = !!data.smtpHost;
      setHasConfig(configExists);
      
      setHost(data.smtpHost || "");
      setPort(data.smtpPort || "587");
      setUser(data.smtpUser || "");
      setPassword(data.smtpPassword ? "••••••••••••••••" : "");
      setSecure(data.smtpSecure === 'SSL' || data.smtpSecure === 'TLS');
      setFromEmail(data.fromEmail || "");
      setFromName(data.fromName || "");
      setRateLimit(data.rateLimit || "60");
      
      setEditing(!configExists);
    } catch (error) {
      console.error("Error loading config:", error);
      setEditing(true);
    }
  };

  const handleSaveConfig = async () => {
    try {
      if (!host || !port || !fromEmail) {
        toast.error("Por favor completa todos los campos obligatorios");
        return;
      }

      setSaving(true);

      const smtpSecureValue = secure ? 'TLS' : 'NONE';

      await api.post("/api/email-config", {
        smtpHost: host,
        smtpPort: parseInt(port),
        smtpUser: user || '',
        smtpPassword: password.includes("•") ? undefined : password,
        smtpSecure: smtpSecureValue,
        fromEmail,
        fromName,
        rateLimit: parseInt(rateLimit)
      });

      toast.success("Configuración guardada exitosamente");
      setEditing(false);
      loadConfig();
    } catch (error) {
      console.error("Error saving config:", error);
      toast.error("Error al guardar la configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    setEditing(true);
    setPassword("");
  };

  const handleCancelEdit = () => {
    setEditing(false);
    loadConfig();
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setConnectionStatus(null);

      const { data } = await api.post("/api/email-config/test");

      setConnectionStatus({
        success: data.success,
        message: data.message
      });

      if (data.success) {
        toast.success("Conexión exitosa con el servidor SMTP");
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error("Error testing connection:", error);
      setConnectionStatus({
        success: false,
        message: error.response?.data?.error || "Error al probar la conexión"
      });
      toast.error("Error al probar la conexión");
    } finally {
      setTesting(false);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Configuración SMTP</Title>
        <MainHeaderButtonsWrapper>
          {!editing && hasConfig && (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleEdit}
              startIcon={<EditIcon />}
              style={{ marginRight: 8 }}
            >
              Editar
            </Button>
          )}
          {editing && hasConfig && (
            <Button
              variant="outlined"
              onClick={handleCancelEdit}
              startIcon={<CancelIcon />}
              style={{ marginRight: 8 }}
            >
              Cancelar
            </Button>
          )}
          <Button
            variant="outlined"
            color="primary"
            onClick={handleTestConnection}
            disabled={testing || editing}
            startIcon={testing ? <CircularProgress size={20} /> : <RefreshIcon />}
            style={{ marginRight: 8 }}
          >
            Probar Conexión
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveConfig}
            disabled={saving || !editing}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            Guardar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card className={classes.configCard}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Configuración del Servidor SMTP
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Configura tu servidor SMTP para enviar campañas de email masivas.
                </Typography>

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Host SMTP *"
                  value={host}
                  onChange={(e) => setHost(e.target.value)}
                  margin="normal"
                  helperText="Ejemplo: smtp.gmail.com"
                  disabled={!editing}
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Puerto *"
                  value={port}
                  onChange={(e) => setPort(e.target.value)}
                  margin="normal"
                  type="number"
                  helperText="Común: 587 (TLS), 465 (SSL), 25 (sin cifrado)"
                  disabled={!editing}
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Usuario"
                  value={user}
                  onChange={(e) => setUser(e.target.value)}
                  margin="normal"
                  helperText="Tu usuario o email SMTP (opcional para MailHog)"
                  disabled={!editing}
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Contraseña"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  type="password"
                  helperText="Tu contraseña SMTP (opcional para MailHog)"
                  disabled={!editing}
                  placeholder={!editing && hasConfig ? "••••••••••••••••" : ""}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={secure}
                      onChange={(e) => setSecure(e.target.checked)}
                      color="primary"
                      disabled={!editing}
                    />
                  }
                  label="Usar SSL/TLS"
                  style={{ marginTop: 16 }}
                />

                <Divider style={{ marginTop: 16, marginBottom: 16 }} />

                <Typography variant="h6" gutterBottom>
                  Información del Remitente
                </Typography>

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Email Remitente *"
                  value={fromEmail}
                  onChange={(e) => setFromEmail(e.target.value)}
                  margin="normal"
                  helperText="Email que aparecerá como remitente"
                  disabled={!editing}
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Nombre Remitente"
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                  margin="normal"
                  helperText="Nombre que aparecerá como remitente"
                  disabled={!editing}
                />

                <Divider style={{ marginTop: 16, marginBottom: 16 }} />

                <Typography variant="h6" gutterBottom>
                  Control de Envío
                </Typography>

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Límite de Tasa (emails/minuto)"
                  value={rateLimit}
                  onChange={(e) => setRateLimit(e.target.value)}
                  margin="normal"
                  type="number"
                  helperText="Número máximo de emails por minuto (recomendado: 60)"
                  disabled={!editing}
                />

                {connectionStatus && (
                  <Box
                    className={`${classes.statusBox} ${
                      connectionStatus.success
                        ? classes.statusSuccess
                        : classes.statusError
                    }`}
                  >
                    {connectionStatus.success ? (
                      <CheckCircleIcon />
                    ) : (
                      <ErrorIcon />
                    )}
                    <Typography variant="body2">
                      {connectionStatus.message}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Guía de Configuración
                </Typography>
                <Typography variant="body2" paragraph>
                  Sigue estos pasos para configurar tu servidor SMTP:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <EmailIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="1. Obtén credenciales SMTP"
                      secondary="Contacta a tu proveedor de email o usa servicios como Gmail, SendGrid, Mailgun"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <EmailIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="2. Configura el host y puerto"
                      secondary="Ingresa el servidor SMTP y el puerto apropiado"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <EmailIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="3. Ingresa tus credenciales"
                      secondary="Usuario y contraseña proporcionados por tu proveedor"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <EmailIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="4. Prueba la conexión"
                      secondary="Usa el botón 'Probar Conexión' para verificar"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            <Card style={{ marginTop: 16 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Proveedores Comunes
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="MailHog (Pruebas Locales)"
                      secondary="localhost:1025 (Sin autenticación)"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Gmail"
                      secondary="smtp.gmail.com:587 (TLS)"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Outlook/Hotmail"
                      secondary="smtp-mail.outlook.com:587 (TLS)"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="SendGrid"
                      secondary="smtp.sendgrid.net:587 (TLS)"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Mailgun"
                      secondary="smtp.mailgun.org:587 (TLS)"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            <Card style={{ marginTop: 16 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Estado del Sistema
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Servidor SMTP"
                      secondary={connectionStatus?.success ? "Conectado" : "No conectado"}
                    />
                    {connectionStatus?.success ? (
                      <CheckCircleIcon color="primary" />
                    ) : (
                      <ErrorIcon color="disabled" />
                    )}
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Configuración"
                      secondary={host && fromEmail ? "Completa" : "Incompleta"}
                    />
                    {host && fromEmail ? (
                      <CheckCircleIcon color="primary" />
                    ) : (
                      <ErrorIcon color="disabled" />
                    )}
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </MainContainer>
  );
};

export default EmailConfig;
